import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { kstToday } from '@shared/utils/kstTime.util';

import {
  addMonths,
  currentYm,
  DateRange,
  daysBetween,
  isCurrentPeriod,
  monthSpanOf,
  PERIOD_UNIT_LABELS,
  PeriodUnit,
  periodLabel,
  rangeOfPeriod,
} from './analyticsPeriod';

//* 세 탭이 같은 기간 필터를 쓴다 (부고=발인일, 화환=판매일, 답례품=주문일).
//* 단위(월/2개월/분기/반기)를 고르고 ◀▶로 앞뒤 기간을 넘기는 방식 —
//* "지난 분기가 어땠지"를 클릭 두 번으로 보게 하는 게 목적이다.

export type { DateRange } from './analyticsPeriod';
export { rangeOfPeriod, currentYm } from './analyticsPeriod';

/** 첫 진입 기본값 — 이번 달 */
export const defaultRange = (): DateRange => rangeOfPeriod(currentYm(), 'month');

/**
 * ◀▶ 연타 방어.
 *
 * 이 필터가 물린 조회는 한 번에 문서 2만 건짜리다. 클릭마다 즉시 쏘면 "반기 전으로"
 * 다섯 번 누르는 동안 그 조회가 다섯 개 동시에 서버에 물린다(요청 취소도 없다).
 * 단위·라벨은 로컬 state라 즉시 바뀌므로 화면이 굼떠 보이지도 않는다.
 */
const PERIOD_APPLY_DEBOUNCE_MS = 350;

export const AnalyticsDateFilter: FC<{
  /** 축 이름 — '발인일' / '판매일' / '주문일' */
  label: string;
  range: DateRange;
  onChange: (next: DateRange) => void;
  maxRangeDays: number;
}> = function AnalyticsDateFilter({ label, range, onChange, maxRangeDays }) {
  const [unit, setUnit] = useState<PeriodUnit>('month');
  const [anchor, setAnchor] = useState<string>(() => currentYm());
  //* 직접선택 입력값 — 조회 버튼을 눌러야 반영된다
  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);
  const [error, setError] = useState<string | null>(null);

  //* onChange는 렌더마다 새 함수일 수 있어 ref로 고정한다 (타이머가 옛 함수를 붙들지 않게)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  //* 언마운트 뒤에 타이머가 살아나 조회를 깨우지 않게
  useEffect(() => cancelPending, [cancelPending]);

  const applyPeriod = (nextAnchor: string, nextUnit: PeriodUnit) => {
    setError(null);
    const next = rangeOfPeriod(nextAnchor, nextUnit);
    cancelPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onChangeRef.current(next);
    }, PERIOD_APPLY_DEBOUNCE_MS);
  };

  const onUnitClick = (next: PeriodUnit) => {
    setUnit(next);
    setError(null);
    if (next === 'custom') {
      cancelPending();
      setDraftFrom(range.from);
      setDraftTo(range.to);
      return;
    }
    applyPeriod(anchor, next);
  };

  /** ◀▶ — 단위만큼 통째로 이동 */
  const step = (direction: -1 | 1) => {
    const next = addMonths(anchor, monthSpanOf(unit) * direction);
    setAnchor(next);
    applyPeriod(next, unit);
  };

  const goCurrent = () => {
    const next = currentYm();
    setAnchor(next);
    applyPeriod(next, unit);
  };

  const onCustomApply = () => {
    if (!draftFrom || !draftTo) {
      setError('시작일과 종료일을 모두 입력해주세요.');
      return;
    }
    if (draftFrom > draftTo) {
      setError('시작일이 종료일보다 뒤입니다.');
      return;
    }
    if (daysBetween(draftFrom, draftTo) > maxRangeDays) {
      setError(`최대 ${maxRangeDays}일까지 조회할 수 있습니다.`);
      return;
    }
    setError(null);
    cancelPending();
    onChange({ from: draftFrom, to: draftTo });
  };

  const atCurrent = isCurrentPeriod(anchor, unit);

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="w-11 shrink-0 text-13 font-medium text-gray-600">{label}</span>
        {(Object.keys(PERIOD_UNIT_LABELS) as PeriodUnit[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onUnitClick(key)}
            className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
              unit === key
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {PERIOD_UNIT_LABELS[key]}
          </button>
        ))}
      </div>

      {unit === 'custom' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="date"
            value={draftFrom}
            max={kstToday()}
            onChange={(e) => setDraftFrom(e.target.value)}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-13"
          />
          <span className="text-13 text-gray-500">~</span>
          <input
            type="date"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            className="rounded-lg border border-gray-300 px-2 py-1.5 text-13"
          />
          <button
            type="button"
            onClick={onCustomApply}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-13 font-medium text-white hover:bg-indigo-700"
          >
            조회
          </button>
          <span className="text-13 tabular-nums text-gray-600">
            {range.from} ~ {range.to}
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="이전 기간"
            className="rounded-lg bg-gray-100 px-3 py-1.5 text-13 font-medium text-gray-700 hover:bg-gray-200"
          >
            ◀
          </button>
          <span className="min-w-[10rem] text-center text-15 font-bold tabular-nums text-gray-900">
            {periodLabel(anchor, unit)}
          </span>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="다음 기간"
            //* 미래 기간은 볼 게 없으니 현재 기간에선 막는다
            disabled={atCurrent}
            className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
              atCurrent
                ? 'cursor-default bg-gray-50 text-gray-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            ▶
          </button>
          {!atCurrent && (
            <button
              type="button"
              onClick={goCurrent}
              className="rounded-lg bg-white px-3 py-1.5 text-13 font-medium text-indigo-700 hover:bg-indigo-50"
            >
              현재
            </button>
          )}
          <span className="text-13 tabular-nums text-gray-500">
            {range.from} ~ {range.to}
          </span>
        </div>
      )}
      {error && <p className="text-13 text-red-600">{error}</p>}
    </>
  );
};
